import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faArrowRightLong,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import {
  GoogleMap,
  LoadScript,
  Marker,
  DirectionsRenderer,
} from '@react-google-maps/api';
import { Spinner } from 'react-bootstrap';

import { useApi } from '../hooks/useApi';
import useConfigs from '../hooks/useConfigs';
import { getUserLocation } from '../utils/getUserLocation';
import useThemes from '../hooks/useThemes';

const containerStyle = {
  width: '100%',
  height: '700px',
};

const API_KEY = 'AIzaSyAAVLRMJVaQQGs4RiJP4m_C5_sZgm3tc5c';
const center = {
  lat: 4.570868,
  lng: -74.297333,
};

export const Sedes = () => {
  const { sedes, getSedes } = useApi();
  const { fontSizeMultiplier, activeTheme } = useConfigs();
  const {
    backgroundOctavo,
    colorSeptimo,
    colorSecundario,
    colorCuarto,
    colorPrincipal,
  } = useThemes();

  const [deptoSelected, setDeptoSelected] = useState(null);
  const [departamentos, setDepartamentos] = useState([]);
  const [map, setMap] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [markerInfo, setMarkerInfo] = useState(null);
  const [directionResponse, setDirectionResponse] = useState(null);

  useEffect(() => {
    let departamentos = sedes.data.map((item) => item.depto);
    const aux = new Set(departamentos.map(JSON.stringify));
    departamentos = Array.from(aux).map(JSON.parse);

    const arr = [];
    departamentos.forEach((item) => {
      const sedesPorDepartamento = sedes.data.filter(
        (sede) => sede.depto.depto_nombre === item.depto_nombre && { ...sede }
      );

      arr.push({
        departamento: { ...item },
        sedes: sedesPorDepartamento.sort((a, b) =>
          a.nombre.localeCompare(b.nombre)
        ),
      });
    });

    setDepartamentos(
      arr.sort((a, b) =>
        a.departamento.depto_nombre.localeCompare(b.departamento.depto_nombre)
      )
    );
  }, [sedes.data]);

  useEffect(() => {
    getUserLocation()
      .then((res) => {
        setUserLocation({ lat: res[0], lng: res[1] });
      })
      .catch((error) => console.error(error));
  }, []);

  const handleClick = (item) => {
    setDeptoSelected((prevState) => {
      if (prevState === item.departamento.depto_nombre) {
        return null;
      } else {
        return item.departamento.depto_nombre;
      }
    });
  };

  const calculateRoute = async () => {
    try {
      // eslint-disable-next-line no-undef
      const directionService = new google.maps.DirectionsService();

      // eslint-disable-next-line no-undef
      const user = new google.maps.LatLng(userLocation.lat, userLocation.lng);

      // eslint-disable-next-line no-undef
      const sede = new google.maps.LatLng(
        markerInfo?.latitud,
        markerInfo?.longitud
      );

      const result = await directionService.route({
        origin: user,
        destination: sede,
        // eslint-disable-next-line no-undef
        travelMode: google.maps.TravelMode.DRIVING,
      });

      setDirectionResponse(result);
      setMarkerInfo(null);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getSedes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Container>
        <MapContainer>
          <LoadScript googleMapsApiKey={API_KEY}>
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={userLocation || center}
              zoom={markerInfo || userLocation ? 14 : 7}
              onLoad={(map) => setMap(map)}
            >
              {directionResponse && (
                <DirectionsRenderer directions={directionResponse} />
              )}
              {sedes.data.map((item) => (
                <Marker
                  key={item.id}
                  position={{ lat: item.latitud, lng: item.longitud }}
                  icon={{ url: 'img/Sedes/pin-sedes-64px.png' }}
                />
              ))}
            </GoogleMap>
          </LoadScript>

          {/* Marker info */}
          {markerInfo && (
            <MarkerInfo
              fontsizemultiplier={fontSizeMultiplier}
              background={colorSeptimo}
              colortitle={colorSecundario}
              colorinfo={colorCuarto}
              colorbtn={colorPrincipal}
            >
              <div className='imagen'>
                <img src={markerInfo.imagen} alt={markerInfo.nombre} />
              </div>
              <div className='texto'>
                <p className='titulo'>{markerInfo.nombre}</p>

                <div className='info'>
                  <h4>Dirección</h4>
                  <p>{markerInfo.direccion}</p>
                </div>

                <div className='info'>
                  <h4>Teléfono</h4>
                  <p>{markerInfo.telefonos}</p>
                </div>

                <div className='info'>
                  <h4>Horario de atención</h4>
                  <p>{markerInfo.horario}</p>
                </div>

                <div className='button' onClick={calculateRoute}>
                  <button>¿Como llegar?</button>
                  <FontAwesomeIcon
                    icon={faArrowRightLong}
                    color={colorPrincipal}
                    size='lg'
                  />
                </div>
              </div>

              <div className='cerrar' onClick={() => setMarkerInfo(null)}>
                <FontAwesomeIcon
                  icon={faXmark}
                  color={colorSecundario}
                  size='2x'
                />
              </div>
            </MarkerInfo>
          )}
        </MapContainer>
        <SedesContainer
          fontsizemultiplier={fontSizeMultiplier}
          background={backgroundOctavo}
          theme={activeTheme}
        >
          <h3>Sedes de atención</h3>
          <hr />
          <div style={{ marginTop: '15px' }}>
            {sedes.isLoading ? (
              <div>
                <Spinner
                  animation='grow'
                  style={{ marginRight: '1rem' }}
                  variant='light'
                />
                <Spinner
                  animation='grow'
                  style={{ marginRight: '1rem' }}
                  variant='light'
                />
                <Spinner animation='grow' variant='light' />
              </div>
            ) : (
              departamentos.map((item, index) => (
                <div key={index}>
                  <Item
                    onClick={() => {
                      setMarkerInfo(null);
                      handleClick(item);
                    }}
                    fontsizemultiplier={fontSizeMultiplier}
                    theme={activeTheme}
                  >
                    <p>{item?.departamento?.depto_nombre}</p>
                    <FontAwesomeIcon
                      icon={
                        deptoSelected !== item.departamento.depto_nombre
                          ? faChevronDown
                          : faChevronUp
                      }
                      color='#fff'
                    />
                  </Item>
                  {deptoSelected === item.departamento.depto_nombre && (
                    <div style={{ marginBottom: '5px' }}>
                      {item.sedes.map((item) => (
                        <div className='ips-container' key={item.id}>
                          <div
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <div
                              className='ips-marker'
                              style={{
                                backgroundColor:
                                  item.id === markerInfo?.id
                                    ? '#fff'
                                    : 'transparent',
                              }}
                            ></div>
                            <P
                              onClick={() => {
                                setMarkerInfo(item);
                                map.panTo({
                                  lat: item.latitud,
                                  lng: item.longitud,
                                });
                              }}
                              fontsizemultiplier={fontSizeMultiplier}
                              theme={activeTheme}
                              key={item.id}
                            >
                              {item.nombre}
                            </P>
                          </div>

                          {item.id === markerInfo?.id && (
                            <Servicios
                              fontsizemultiplier={fontSizeMultiplier}
                              theme={activeTheme}
                              dangerouslySetInnerHTML={{
                                __html: item?.servicios,
                              }}
                            ></Servicios>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </SedesContainer>
      </Container>
    </>
  );
};

// ##### Estilos #####
const Container = styled.div`
  width: 95%;
  display: flex;
  flex-direction: row;
  border-top-right-radius: 50px;
  border-bottom-right-radius: 50px;
  margin-top: 5vh;
  padding-bottom: 5rem;
  overflow: hidden;
  @media (max-width: 768px) {
    margin-top: 3rem;
    flex-direction: column;
  }
`;

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 700px;
  @media (max-width: 768px) {
    display: grid !important;
    width: 100%;
    min-height: 400px;
  }
`;

const SedesContainer = styled.div`
  width: 40%;
  padding: 3rem 3rem;
  border-top-right-radius: 25 !important;
  border-top-left-radius: 25 !important;
  background-color: ${(props) => props.background};
  border-radius: 0 0 50px 0;

  .ips-container {
    /* display: flex; */
    /* align-items: center; */
    margin-bottom: 1rem;
  }

  .ips-marker {
    width: 10px;
    height: 10px;
  }

  h3 {
    font-size: ${(props) => 50 * props.fontsizemultiplier};
    color: ${(props) => props.theme?.color_base_blanco};
  }

  hr {
    width: 100%;
    background-color: ${(props) => props.theme?.color_base_blanco};
  }
  @media (max-width: 768px) {
    width: 100%;
    min-height: 250px;
    h3 {
      font-size: ${(props) => 45 * props.fontsizemultiplier};
      color: ${(props) => props.theme?.color_base_blanco};
    }
  }
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  cursor: pointer;

  p {
    font-size: ${(props) => 16 * props.fontsizemultiplier};
    color: ${(props) => props.theme?.color_base_blanco};
  }
`;

const P = styled.p`
  font-size: ${(props) => 14 * props.fontsizemultiplier};
  margin: auto 0 auto 10px;
  margin-top: 0;
  cursor: pointer;
  color: ${(props) => props.theme?.color_base_blanco};

  &:hover {
    filter: brightness(1.5);
  }
`;

const MarkerInfo = styled.div`
  position: absolute;
  top: 5%;
  left: 50%;
  right: 0;
  margin-left: -30%;
  display: flex;
  width: 60%;
  border-radius: 2rem;
  overflow: hidden;
  background-color: ${(props) => props.background};

  @media (max-width: 1023px) {
    top: 10%;
    left: 50%;
    right: 0;
    margin-left: -45%;
    width: 90%;
  }

  .imagen {
    width: 40%;
    background-color: #e5e5e5;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    @media (max-width: 1023px) {
      display: none;
    }
  }

  .texto {
    width: 60%;
    padding: 1rem 2rem 0.5rem 2rem;

    @media (max-width: 1023px) {
      width: 100%;
    }
  }

  .titulo {
    padding-right: 2rem;
    font-size: ${(props) => 20 * props.fontsizemultiplier};
    font-weight: 700;
    color: ${(props) => props.colortitle};

    @media (max-width: 1023px) {
      padding-right: 0.5rem;
      font-size: ${(props) => 15 * props.fontsizemultiplier};
    }
  }

  .info {
    margin-bottom: 1rem;

    h4 {
      font-size: ${(props) => 14 * props.fontsizemultiplier};
      text-transform: uppercase;
      color: ${(props) => props.colortitle};
    }

    p {
      color: ${(props) => props.colorinfo};
    }
  }

  .button {
    display: flex;
    justify-content: space-between;
    align-items: center;
    /* padding-top: 0.5rem; */
    cursor: pointer;
    border-top: 1px solid ${(props) => props.colorbtn};

    button {
      border: none;
      background-color: transparent;
      font-weight: 700;
      color: ${(props) => props.colorbtn};
    }
  }

  .cerrar {
    position: absolute;
    top: 2%;
    right: 2%;
    cursor: pointer;

    &:hover {
      filter: brightness(1.2);
    }
  }
`;

const Servicios = styled.div`
  margin-top: 1rem;
  border-bottom: 2px solid #fff;
  font-family: 'Myriad Pro' !important;
  color: #fff;

  ul {
    display: flex;
    flex-direction: column;

    li {
      font-size: ${(props) => 15 * props.fontsizemultiplier};
      font-weight: 500;

      span {
        font-family: 'Myriad Pro' !important;
      }

      &::before {
        content: '-';
        margin-right: 1rem;
      }
    }
  }
`;
